// @flow

import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { components } from '@peachjar/components';
import { TextField } from '@material-ui/core';
import { styles } from './WebsiteButtonRequestTab.css';
import { Mutation } from 'react-apollo';
import {
  setWebmasterEmail,
  sendWebmasterEmail,
  toggleWebmasterSendDisable,
  toggleWebmasterEmailSent,
} from '../setup-school.duck';
import VISIT_DISTRICT_SETUP_TASK_MUTATION from "../../_shared/gql/visitDistrictSetupTask.mutation";

const {
  Buttons: { ButtonPrimaryLarge },
  Notifications: { notifyError, notifySuccess },
  LoadingIndicators: { Spinner },
  TextInput,
} = components;

export const STAFF_SETUP_ROUTE = '/onboarding/setup/staff';

type ReduxDispatchProps = {|
  +handleWebmasterEmailChange: (event: any) => void,
  +handleWebmasterEmailSubmit: (
    markSiteLinkedVisited: any,
    variables: any,
    onNotifyError: any
  ) => any,
  +handleWebmasterEmailSubmitSuccess: () => void,
  +onNotifyError: (error: any) => void,
|};

type ReduxStateProps = {|
  +webmasterEmail: string,
  +webmasterSendDisable: boolean,
  +webmasterEmailSent: boolean,
|};

type Props = {|
  history: any,
  ...ReduxDispatchProps,
  ...ReduxStateProps,
  +ncesDistrictId: string,
  +registrant: any,
  +contactPhoneNumber: string,
  +isSiteLinked: boolean,
|};

const createWebmasterEmailContent = (
  { firstName, lastName, email },
  contactPhoneNumber
) => ({
  subject: 'Placement of Peachjar buttons',
  block1: 'Hello!',
  block2: `We're excited to begin implementing Peachjar in our district! To start that process, you've been designated as the person in charge of placing the Peachjar buttons on each of your schools' websites. These buttons will link to each schools’ Peachjar flyerboard where parents and staff will be able to click to see all active flyers for their school.`,
  block3: `Attached you will find a CSV file with a list of your schools and a snippet of code for the Peachjar button that contains a link to each school's Peachjar flyerboard.`,
  buttonText: 'Download CSV',
  block4_1: "If you use Blackboard Web Community Manager, you can follow these instructions and click ",
  block4_2: "download the app you want",
  block4_2_href: "https://help.blackboard.com/Web_Community_Manager/Administrator/Configure/Apps/Custom_Apps",
  block4_3: ` to find Peachjar. If you need assistance adding the Peachjar buttons through Blackboard contact us at (858) 997-2117.`,
  block5: 'Best regards,',
  registrantName: `${firstName} ${lastName}`, // TODO: Get this value from Redux
  registrantPhone: contactPhoneNumber, // TODO: Get this value from Redux
  registrantEmail: email, // TODO: Get this value from Redux
});

export const WebsiteButtonRequest = ({
  webmasterEmail,
  webmasterSendDisable,
  webmasterEmailSent,
  handleWebmasterEmailChange,
  handleWebmasterEmailSubmit,
  handleWebmasterEmailSubmitSuccess,
  history,
  onNotifyError,
  ncesDistrictId,
  registrant,
  contactPhoneNumber,
  isSiteLinked,
}: Props) => {
  const onContinueClick = () => {
    history.push(STAFF_SETUP_ROUTE);
  };

  const webmasterEmailContent = createWebmasterEmailContent(
    registrant,
    contactPhoneNumber
  );

  return (
    <div>
      <div className={styles.wrapper}>
        <h3 className={styles.pageHeader}>
          Send an Email Request to Your Webmaster
        </h3>
        <p className={styles.pageText}>
          Use the email below to send instructions to your Webmaster on
          connecting Peachjar flyers to each school&apos;s website. Simply, add
          their email address and send!
        </p>
        <p className={styles.label}>Email Subject</p>
        <div className={styles.emailSubject}>
          <p className={styles.emailSubjectText}>
            {webmasterEmailContent.subject}
          </p>
        </div>
        <p className={styles.label}>Message</p>
        <div className={styles.emailMessage}>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block1}
          </p>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block2}
          </p>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block3}
          </p>
          <ButtonPrimaryLarge disabled style={{ marginBottom: '1rem' }}>
            {webmasterEmailContent.buttonText}
          </ButtonPrimaryLarge>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block4_1}
          </p>
          <p className={styles.emailMessageText}>
            <a href={webmasterEmailContent.block4_2_href}>
              {webmasterEmailContent.block4_2}
            </a>
          </p>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block4_3}
          </p>
          <p className={styles.emailMessageText}>
            {webmasterEmailContent.block5}
          </p>
          <span>{webmasterEmailContent.registrantName}</span>
          <br />
          <span className={styles.emailSmallSignatureText}>
            {webmasterEmailContent.registrantPhone}
          </span>
          <br />
          <span className={styles.emailSmallSignatureText}>
            {webmasterEmailContent.registrantEmail}
          </span>
        </div>

        <Mutation
          mutation={VISIT_DISTRICT_SETUP_TASK_MUTATION}
          onError={onNotifyError}
          onCompleted={handleWebmasterEmailSubmitSuccess}
        >
          {(markSiteLinkedVisited, { loading, error, data }) => {
            const variables = {
              input: {
                ncesDistrictId,
                taskName: 'siteLinked',
              },
            };

            return (
              <div className={styles.emailInputWrapper}>
                <TextInput
                  type="email"
                  className={styles.emailInput}
                  label="Webmaster Email Address"
                  placeholder={webmasterEmail}
                  onChange={handleWebmasterEmailChange}
                  data-testid="field-webmasterEmail"
                />
                <ButtonPrimaryLarge
                  disabled={webmasterSendDisable}
                  onClick={handleWebmasterEmailSubmit(
                    markSiteLinkedVisited,
                    variables
                  )}
                  data-testid="click-webmasterEmailSubmit"
                >
                  {loading && <Spinner />}Send
                </ButtonPrimaryLarge>
              </div>
            );
          }}
        </Mutation>

        <div className={styles.continueButtonWrapper}>
          <ButtonPrimaryLarge
            id="continueToStaffBtn"
            disabled={!webmasterEmailSent && !isSiteLinked}
            onClick={onContinueClick}
          >
            Continue to Staff Setup
          </ButtonPrimaryLarge>
        </div>
      </div>
    </div>
  );
};

const SUCCESS_MESSAGE = 'Your email is on its way!';

const mapStateToProps = (state): ReduxStateProps => ({
  webmasterEmail: state.setupSchool.webmasterEmail,
  webmasterSendDisable: state.setupSchool.webmasterSendDisable,
  webmasterEmailSent: state.setupSchool.webmasterEmailSent,
});

const mapDispatchToProps = (dispatch): ReduxDispatchProps => ({
  handleWebmasterEmailChange: event => {
    dispatch(setWebmasterEmail(event.target.value));
  },
  onNotifyError: error => {
    dispatch(notifyError(error.message));
  },
  handleWebmasterEmailSubmit: (
    markSiteLinkedVisited,
    variables,
    onNotifyError
  ) => async () => {
    try {
      await dispatch(sendWebmasterEmail());
      markSiteLinkedVisited({ variables });
    } catch (err) {
      onNotifyError(`Encountered an error while dispatching the email. ${err}`);
    }
  },
  handleWebmasterEmailSubmitSuccess: () => {
    dispatch(notifySuccess(SUCCESS_MESSAGE)); // TODO: Move dispatching notifySuccess to sendWebmasterEmail Saga after response is successful
    dispatch(toggleWebmasterSendDisable(true));
    dispatch(toggleWebmasterEmailSent(true));
  },
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(WebsiteButtonRequest)
);



// WEBPACK FOOTER //
// ./src/Onboarding/Setup/school/containers/WebsiteButtonRequestTab.jsx